import React, { useState, useEffect } from 'react';
import { Table } from '../types';
import { tablesAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useCurrency } from '../contexts/CurrencyContext';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { Button, Badge } from './ui';

const TableGrid: React.FC = () => {
  const [tables, setTables] = useState<Table[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { formatPrice } = useCurrency();
  const { theme, toggleTheme, isDark } = useTheme();

  useEffect(() => {
    loadTables();
    
    // Masa durumlarını otomatik yenile (30 saniyede bir)
    const interval = setInterval(() => {
      loadTables();
    }, 30000);
    
    return () => clearInterval(interval);
  }, []);

  const loadTables = async () => {
    try {
      const data = await tablesAPI.getAll();
      setTables(data);
    } catch (error) {
      toast.error('Masalar yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const getTableStatusColor = (status: string) => {
    switch (status) {
      case 'boş':
        return 'bg-green-100 dark:bg-green-900/20 border-green-300 dark:border-green-700 hover:bg-green-200 dark:hover:bg-green-900/40 text-green-800 dark:text-green-300';
      case 'dolu':
        return 'bg-red-100 dark:bg-red-900/20 border-red-300 dark:border-red-700 hover:bg-red-200 dark:hover:bg-red-900/40 text-red-800 dark:text-red-300';
      default:
        return 'bg-gray-100 dark:bg-gray-800 border-gray-300 dark:border-gray-600 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-800 dark:text-gray-200';
    }
  };

  const getTableStatusText = (status: string) => {
    switch (status) {
      case 'boş':
        return 'Boş';
      case 'dolu':
        return 'Dolu';
      default:
        return 'Bilinmiyor';
    }
  };

  const getTableStatusIcon = (status: string) => {
    switch (status) {
      case 'boş':
        return '🟢';
      case 'dolu':
        return '🔴';
      default:
        return '⚪';
    }
  };

  const handleTableClick = (table: Table) => {
    if (table.status === 'boş') {
      // Boş masaya yeni sipariş başlat
      navigate(`/order/${table.id}`);
    } else {
      // Dolu masanın mevcut siparişini göster
      navigate(`/order/${table.id}/${table.current_order?.id}`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-cafe-cream dark:bg-dark-bg transition-colors duration-500">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cafe-brown"></div>
          <span className="ml-2 text-cafe-brown dark:text-cafe-light">{''}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cafe-cream dark:bg-dark-bg transition-colors duration-500">
      <div className="container mx-auto px-2 sm:px-4 py-3 sm:py-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 space-y-3 sm:space-y-0">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-cafe-brown dark:text-cafe-light">🍽️ Masalar</h1>
            <p className="text-sm sm:text-base text-gray-600 dark:text-dark-text mt-1">Hoş geldiniz, {user?.username} ({user?.role})</p>
          </div>
          <div className="flex flex-wrap items-center gap-2 sm:gap-3 w-full sm:w-auto justify-end">
            <Button
              variant="secondary"
              size="sm"
              onClick={toggleTheme}
              title={''}
            >
              {isDark ? '☀️' : '🌙'}
            </Button>
            <Button
              variant="success"
              size="sm"
              onClick={() => navigate('/')}
              className="text-sm"
            >
              <span className="hidden sm:inline">🚀 {''}</span>
              <span className="sm:hidden">🚀</span>
            </Button>
            {user?.role === 'admin' && (
              <Button
                variant="info"
                size="sm"
                onClick={() => navigate('/admin')}
                className="text-sm"
              >
                <span className="hidden sm:inline">⚙️ {''}</span>
                <span className="sm:hidden">⚙️</span>
              </Button>
            )}
            <Button
              variant="primary"
              size="sm"
              onClick={loadTables}
              className="text-sm"
            >
              <span className="hidden sm:inline">🔄 {''}</span>
              <span className="sm:hidden">🔄</span>
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={logout}
              className="text-sm"
            >
              <span className="hidden sm:inline">🚪 {''}</span>
              <span className="sm:hidden">🚪</span>
            </Button>
          </div>
        </div>


        {/* Özel Masa Düzeni */}
        <div className="flex gap-4 justify-between">
          {/* SOL: T1-T8 Dikey */}
          <div className="flex flex-col gap-2">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((num) => {
              const table = tables.find(t => t.table_number === num);
              return (
                <div
                  key={`t${num}`}
                  onClick={() => table && handleTableClick(table)}
                  className={`
                    cursor-pointer border-2 rounded p-2 w-24 h-14 flex flex-col items-center justify-center
                    ${table ? getTableStatusColor(table.status) : 'bg-gray-200 dark:bg-gray-700 border-gray-400'}
                  `}
                >
                  <div className="font-bold text-sm">T{num}</div>
                  <div className="text-xs">{table ? getTableStatusText(table.status) : 'Yok'}</div>
                  {table?.current_order && <div className="text-xs font-semibold">{formatPrice(table.current_order.total_amount)}</div>}
                </div>
              );
            })}
          </div>

          {/* ORTA: T9-T18 */}
          <div className="flex flex-col gap-2">
            {/* T9-T17: 3x3 Grid */}
            <div className="grid grid-cols-3 gap-2">
              {[9, 12, 15, 10, 13, 16, 11, 14, 17].map((num) => {
                const table = tables.find(t => t.table_number === num);
                return (
                  <div
                    key={`t${num}`}
                    onClick={() => table && handleTableClick(table)}
                    className={`
                      cursor-pointer border-2 rounded p-2 w-24 h-14 flex flex-col items-center justify-center
                      ${table ? getTableStatusColor(table.status) : 'bg-gray-200 dark:bg-gray-700 border-gray-400'}
                    `}
                  >
                    <div className="font-bold text-sm">T{num}</div>
                    <div className="text-xs">{table ? getTableStatusText(table.status) : 'Yok'}</div>
                    {table?.current_order && <div className="text-xs font-semibold">{formatPrice(table.current_order.total_amount)}</div>}
                  </div>
                );
              })}
            </div>
            
            {/* T18 Tek */}
            <div className="flex justify-end">
              {(() => {
                const table = tables.find(t => t.table_number === 18);
                return (
                  <div
                    onClick={() => table && handleTableClick(table)}
                    className={`
                      cursor-pointer border-2 rounded p-2 w-24 h-14 flex flex-col items-center justify-center
                      ${table ? getTableStatusColor(table.status) : 'bg-gray-200 dark:bg-gray-700 border-gray-400'}
                    `}
                  >
                    <div className="font-bold text-sm">T18</div>
                    <div className="text-xs">{table ? getTableStatusText(table.status) : 'Yok'}</div>
                    {table?.current_order && <div className="text-xs font-semibold">{formatPrice(table.current_order.total_amount)}</div>}
                  </div>
                );
              })()}
            </div>
          </div>

          {/* SAĞ: C Masaları - Çerçeveli */}
          <div className="border-4 border-gray-400 dark:border-gray-500 rounded-lg p-3">
            <div className="flex gap-4">
              {/* C1-C6 Sol Grup */}
              <div className="grid grid-cols-2 gap-2">
                {[1, 2, 3, 4, 5, 6].map((num) => {
                  const table = tables.find(t => t.table_number === 18 + num);
                  return (
                    <div
                      key={`c${num}`}
                      onClick={() => table && handleTableClick(table)}
                      className={`
                        cursor-pointer border-2 rounded p-2 w-24 h-14 flex flex-col items-center justify-center
                        ${table ? getTableStatusColor(table.status) : 'bg-gray-200 dark:bg-gray-700 border-gray-400'}
                      `}
                    >
                      <div className="font-bold text-sm">C{num}</div>
                      <div className="text-xs">{table ? getTableStatusText(table.status) : 'Yok'}</div>
                      {table?.current_order && <div className="text-xs font-semibold">{formatPrice(table.current_order.total_amount)}</div>}
                    </div>
                  );
                })}
              </div>

              {/* C7-C16 Sağ Grup */}
              <div className="grid grid-cols-2 gap-2">
                {[7, 8, 9, 10, 11, 12, 13, 14, 15, 16].map((num) => {
                  const table = tables.find(t => t.table_number === 18 + num);
                  return (
                    <div
                      key={`c${num}`}
                      onClick={() => table && handleTableClick(table)}
                      className={`
                        cursor-pointer border-2 rounded p-2 w-24 h-14 flex flex-col items-center justify-center
                        ${table ? getTableStatusColor(table.status) : 'bg-gray-200 dark:bg-gray-700 border-gray-400'}
                      `}
                    >
                      <div className="font-bold text-sm">C{num}</div>
                      <div className="text-xs">{table ? getTableStatusText(table.status) : 'Yok'}</div>
                      {table?.current_order && <div className="text-xs font-semibold">{formatPrice(table.current_order.total_amount)}</div>}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {tables.length === 0 && (
          <div className="text-center py-8 sm:py-12">
            <div className="text-4xl sm:text-6xl mb-3 sm:mb-4">🍽️</div>
            <h3 className="text-lg sm:text-xl font-semibold text-gray-600 dark:text-dark-text mb-2">
              Henüz masa bulunamadı
            </h3>
            <p className="text-sm sm:text-base text-gray-500 dark:text-gray-400">
              Sistem yöneticisi tarafından masalar eklenmesi bekleniyor.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TableGrid;
